import { Loader, LoaderType } from '@fable/components'
import { css } from '@fable/theme'
import { Club } from '@fable/types'
import BookListDetailSidebar from 'app/social/components/book_list/BookListDetailSidebar'
import { bookRoute } from 'app/store/routes'
import { Page } from 'components/Page'
import { pageAnchors, PageDetail } from 'components/page_detail'
import { useBookOwned } from 'hooks/useBookOwned'
import { bookSchema, faqSchema } from 'seo'
import { BookDetailHeader } from './BookDetailHeader'
import {
  bookMetadata,
  getBannerText,
  getClubsReadingBook,
  useBookFaq,
} from './helpers'
import {
  BookDetailAuthors,
  // BookDetailClubSuggestions,
  BookDetailFAQ,
  BookDetailStartClubCallout,
  BookDetailReviews,
  BookDetailSimilarBooks,
} from './sections'
import { useLoadBookDetail } from './useLoadBookDetail'
import { BookDetailAboutSectionV2 } from './v2'
import { BookDetailAboutSection } from './BookDetailAboutSection'
import { isWebView } from '@fable/utils'
import { useFeatureFlags } from 'hooks'

const BookDetailPage = () => {
  const { book, similarBooksQuery, clubSuggestionsQuery, bookQuery } =
    useLoadBookDetail()
  const { owned } = useBookOwned({ bookId: book?.id })
  const bannerText = owned ? undefined : getBannerText(book)
  const authorsHidden = !book?.authors.length
  const similarBooksHidden =
    !!similarBooksQuery?.data &&
    !similarBooksQuery?.data?.data?.response?.books?.length

  const clubData = clubSuggestionsQuery?.data?.data
  const clubs = (clubData?.results as Club[]) || []
  const clubsReadingBook = getClubsReadingBook({
    clubs,
    book,
  })
  const hasClubs = !!clubsReadingBook?.length
  const faqQuery = useBookFaq({ book })
  const faqs = faqQuery?.data?.faqList?.listCollection?.items || []
  const faqsHidden = !faqQuery.loading && !faqs?.length
  const flagEnabled = useFeatureFlags((s) => s.flagEnabled)
  const bdpListTest = flagEnabled('bdp_list_cro_test')

  if (bookQuery.isFetching || owned === undefined) {
    return <Loader type={LoaderType.fullscreen} />
  }

  return !book ? (
    <div
      className={css`
        width: 100%;
        min-height: calc(100vh - 60px);
      `}
    />
  ) : (
    <>
      <Page
        head={{
          schema: [bookSchema(book), faqSchema(faqs)],
          ...bookMetadata(book),
          canonical: true,
          url: book.url,
        }}
        className="fade"
      >
        <PageDetail
          className={css`
            padding-bottom: 100px;
          `}
          columns
          url={`${window.location.host}${bookRoute(book)}`}
          category="book"
          giftBookId={book.isbn}
          bannerText={bannerText}
          header={
            <BookDetailHeader
              book={book}
              hasClubs={hasClubs}
              isTestActive={bdpListTest}
              mini={isWebView}
            />
          }
          mini={isWebView}
          sections={[
            {
              ...pageAnchors.about,
              element: bdpListTest ? (
                <BookDetailAboutSectionV2 book={book} hideProps={isWebView} />
              ) : (
                <BookDetailAboutSection book={book} />
              ),
            },
            {
              ...pageAnchors.reviews,
              element: <BookDetailReviews book={book} mini={isWebView} />,
            },
            {
              ...pageAnchors.author,
              element: isWebView ? null : <BookDetailAuthors />,
              hidden: {
                anchor: authorsHidden,
                element: authorsHidden,
              },
            },
            // {
            //   ...pageAnchors.bookClubsReading,
            //   element: <BookDetailClubSuggestions clubs={clubsReadingBook} />,
            //   hidden: {
            //     anchor: !hasClubs,
            //     element: !hasClubs,
            //   },
            // },
            {
              ...pageAnchors.startClub,
              element: isWebView ? null : <BookDetailStartClubCallout />,
            },
            {
              ...pageAnchors.faq,
              element: isWebView ? null : (
                <BookDetailFAQ
                  title={pageAnchors.faq.title}
                  faqs={faqQuery.data?.faqList?.listCollection?.items || []}
                  loading={faqQuery.loading}
                />
              ),
              hidden: {
                anchor: faqsHidden,
                element: faqsHidden,
              },
            },
          ]}
          footer={
            similarBooksHidden || isWebView ? null : (
              <BookDetailSimilarBooks query={similarBooksQuery} />
            )
          }
        />
      </Page>
      <BookListDetailSidebar />
    </>
  )
}

export default BookDetailPage
